/* pages/Listings.jsx — UPDATED */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { MdOutlineLocationOn } from "react-icons/md";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./../style/topratedplaces.css";

const PER_PAGE = 12;

const Listings = () => {
  const { categoryId } = useParams();
  const { state } = useLocation();
  const categoryNameFromNav = state?.categoryName || null;

  const [businesses, setBusinesses] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [categoryName, setCategoryName] = useState(categoryNameFromNav);

  const navigate = useNavigate();

  // fetch category name if not provided in navigation state
  useEffect(() => {
    if (!categoryName) {
      (async () => {
        try {
          const { data } = await axios.get("https://biz.dropyourbusiness.com/api/categories");
          const found = (Array.isArray(data) ? data : []).find((c) => String(c.Category_Id) === String(categoryId));
          if (found) setCategoryName(found.Category_Name);
        } catch {}
      })();
    }
  }, [categoryId, categoryName]);

  const fetchBusinesses = async (page = 1) => {
    setLoading(true);
    try {
      const url = `https://biz.dropyourbusiness.com/api/business/search?category=${categoryId}&per_page=${PER_PAGE}&page=${page}&status=A&is_active=1`;
      const { data } = await axios.get(url);

      // expect same structure as your current list (businesses + pagination)
      setBusinesses(data.businesses || []);
      setPagination({
        ...data.pagination,
        current_page: Number(page),
      });
    } catch (error) {
      console.error("Error fetching business data:", error);
      setBusinesses([]);
      setPagination({ current_page: 1, last_page: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handlePageChange = (page) => {
    if (!page || page < 1 || page > (pagination?.last_page || 1)) return;
    fetchBusinesses(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toPublicPath = (biz) => {
    // If live_url is absolute -> open new tab (handled on click)
    // Else route to /business/:slugOrId inside app
    if (biz?.live_url && /^https?:\/\//i.test(String(biz.live_url))) return null;
    if (biz?.live_url) return `/business/${biz.live_url}`;
    return `/business/${biz.id}`;
  };

  const title = useMemo(
    () => (categoryName ? `${categoryName}` : "Category"),
    [categoryName]
  );

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!businesses.length) {
    return (
      <div className="top-rated-places">
        <h1>{title}</h1>
        <h2>
          Listing <span className="theme-cl">Businesses</span>
        </h2>
        <div className="no-data">No businesses found.</div>
      </div>
    );
  }

  return (
    <div className="top-rated-places">
      <h1>{title}</h1>
      <h2>
        Listing <span className="theme-cl">Businesses</span>
      </h2>

      <div className="card-main">
        <div className="places-grid">
          {businesses.map((business) => {
            const imgSrc =
              (business.images && business.images[0]) ||
              business.logo ||
              "https://snasso.quintbrothers.com/wp-content/uploads/2025/09/defulat-image-scaled.webp";

            const desc = business?.description || "";
            const addr = business?.address || {};
            const locationParts = [
              addr?.city?.name,
              addr?.state?.name,
              addr?.country?.name,
            ].filter(Boolean);
            const street = addr?.street_address_1 ? `${addr.street_address_1}, ` : "";

            const internalPath = toPublicPath(business);
            const isExternal = business?.live_url && /^https?:\/\//i.test(String(business.live_url));

            const onOpen = () => {
              if (isExternal) {
                window.open(business.live_url, "_blank", "noopener,noreferrer");
              } else {
                navigate(internalPath, { state: { business } });
              }
            };

            return (
              <div className="place-card" key={business.live_url || business.id}>
                <img
                  src={imgSrc}
                  alt={business.business_name}
                  className="place-image"
                  style={{ height: 300, width: 350, objectFit: "cover", cursor: "pointer" }}
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://snasso.quintbrothers.com/wp-content/uploads/2025/09/defulat-image-scaled.webp";
                  }}
                  onClick={onOpen}
                />

                <div className="Reveal-listing-price-info">
                  <span className="pricetag">
                    {business?.category?.name || categoryName || "Uncategorized"}
                  </span>
                </div>

                <h3 style={{ cursor: "pointer" }} onClick={onOpen}>
                  {business.business_name}
                </h3>

                <p className="description" style={{ color: "#55667b", fontSize: 15 }}>
                  {expandedDescriptions[business.id]
                    ? desc
                    : `${desc.slice(0, 100)}${desc.length > 100 ? "..." : ""}`}
                  {desc.length > 100 && (
                    <button
                      onClick={() => toggleDescription(business.id)}
                      className="read-more-button-listing"
                    >
                      {expandedDescriptions[business.id] ? "Read Less" : "Read More"}
                    </button>
                  )}
                </p>

                <p
                  style={{
                    color: "#55667b",
                    fontSize: 13,
                    borderTop: "1px solid #e3e3ea",
                    marginTop: 15,
                    paddingTop: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    lineHeight: 1.4,
                  }}
                >
                  <MdOutlineLocationOn />
                  <span>{`${street}${locationParts.join(", ")}`}</span>
                </p>

                <div className="card-footer">
                  <div className="user-info" />
                  <div className="view-button-main" style={{ marginTop: 10 }}>
                    <button className="view-button" onClick={onOpen}>
                      {isExternal ? "Open Website" : "View More"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Simple pagination */}
      <div className="pagination">
        <button
          className="pagination-button"
          onClick={() => handlePageChange(pagination.current_page - 1)}
          disabled={pagination.current_page <= 1}
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {pagination.current_page} of {pagination.last_page}
        </span>
        <button
          className="pagination-button"
          onClick={() => handlePageChange(pagination.current_page + 1)}
          disabled={pagination.current_page >= pagination.last_page}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Listings;
